import { getPlaybook, isPlaybookId } from "./registry";
import type { LoopStage, Playbook, PlaybookId } from "./types";

/**
 * A counterpart is an ordinary participant row in `clients`. Its loop data
 * lives under ONE key of `case_data`, so it can never collide with the crisis
 * incident fields that share that column on other rows.
 */
export const COUNTERPART_KEY = "counterpart";

export interface CounterpartFile {
  playbook: PlaybookId;
  prep: Record<string, string>;
}

export class CounterpartInputError extends Error {}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** The counterpart file stored on a participant, or null for a plain one. */
export function readCounterpart(caseData: unknown): CounterpartFile | null {
  if (!isObject(caseData)) return null;
  const file = caseData[COUNTERPART_KEY];
  if (!isObject(file) || !isPlaybookId(file.playbook)) return null;
  const prep: Record<string, string> = {};
  if (isObject(file.prep))
    for (const [key, value] of Object.entries(file.prep))
      if (typeof value === "string") prep[key] = value;
  return { playbook: file.playbook, prep };
}

/** Validate untrusted prep against the playbook: known fields, strings, capped. */
export function normalizePrep(
  playbook: Playbook,
  input: unknown,
): Record<string, string> {
  if (input === undefined || input === null) return {};
  if (!isObject(input))
    throw new CounterpartInputError("Prep must be an object");
  const fields = new Map(playbook.prep.map((field) => [field.id, field]));
  const prep: Record<string, string> = {};
  for (const [key, value] of Object.entries(input)) {
    const field = fields.get(key);
    if (!field)
      throw new CounterpartInputError(`Unknown prep field "${key}"`);
    if (typeof value !== "string")
      throw new CounterpartInputError(`Prep field "${key}" must be text`);
    const text = value.trim();
    if (text.length > field.maxLength)
      throw new CounterpartInputError(
        `Prep field "${key}" is longer than ${field.maxLength} characters`,
      );
    if (text) prep[key] = text;
  }
  return prep;
}

/**
 * Apply a client's `{ playbook, prep }` to a participant's `case_data`.
 * Every other key in `case_data` is preserved. A playbook cannot change once
 * set: the prep, rehearsals and reports that follow are all shaped by it.
 */
export function mergeCounterpartCaseData(
  existing: unknown,
  input: unknown,
): Record<string, unknown> {
  if (!isObject(input))
    throw new CounterpartInputError("Counterpart data must be an object");
  const current = readCounterpart(existing);
  const playbookId = input.playbook ?? current?.playbook;
  if (!isPlaybookId(playbookId))
    throw new CounterpartInputError("Choose a supported playbook");
  if (current && current.playbook !== playbookId)
    throw new CounterpartInputError(
      "A counterpart's playbook cannot be changed",
    );
  const prep =
    input.prep === undefined
      ? (current?.prep ?? {})
      : normalizePrep(getPlaybook(playbookId), input.prep);
  return {
    ...(isObject(existing) ? existing : {}),
    [COUNTERPART_KEY]: { playbook: playbookId, prep },
  };
}

export function missingPrep(playbook: Playbook, prep: Record<string, string>) {
  return playbook.prep.filter(
    (field) => field.required && !prep[field.id]?.trim(),
  );
}

/** Where this counterpart is in the loop. Later stages arrive with their PRs. */
export function counterpartStage(file: CounterpartFile): LoopStage {
  return missingPrep(getPlaybook(file.playbook), file.prep).length > 0
    ? "prep"
    : "rehearse";
}
